import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLockBodyScroll } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { MainWebShop, } from '@as-react/common-types';
import { findColor, determineSelectedSku } from '@as-react/common-utils';
import { Scrollable, useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop, Box, Breakpoint, BottomSheet, Sidebar, Stack, TextAlpha, useIsClient, Price, } from '@yonderland/andes-react';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { ScrollUtil } from '../../../util/ScrollUtil';
import { generateTestId } from '../../../util/TestUtil';
import AsNumeral from '../../../vendors/asNumeral';
import { MiniPDPDeliveryPromise } from './MiniPDPDeliveryPromise';
import { MiniPDPMultiCTA } from './MiniPDPMultiCTA';
import MiniPDPMultiFooter from './MiniPDPMultiFooter';
import { MiniPDPMultiProductTileList } from './MiniPDPMultiProductTileList';
const TEST_ID = 'mini-pdp-multi';
export const MiniPDPMulti = ({ isOpen, popoverTitle, title, onClose, products, wishlistProducts, deliveryPromise, totalPrice, isPDP, onWishListToggle, onAddToBasket, fetchDeliveryPromise, fetchTotalPrice, }) => {
    const tilesWrapperRef = useRef(null);
    const multiContentRef = useRef(null);
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const aemConfiguration = useAemConfig();
    const { currency, isShopLanding, mainWebShop, feature_toggles } = aemConfiguration;
    const { show_sticky_bar_pdp } = feature_toggles;
    const productsCount = products.length;
    const getInitSelectedProductsData = () => products.map(product => {
        const activeColor = findColor(product.colours, product.colourId) || product.colours[0];
        return {
            product,
            colorId: product.colourId,
            sku: determineSelectedSku(activeColor),
            quantity: 1,
            isSelected: false,
        };
    });
    const isClient = useIsClient();
    const isMobile = useMediaQueryMatchesOnMobile({ isSsr: !isClient });
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const isTablet = !isMobile && !isDesktop;
    const [selectedProductsData, setSelectedProductsData] = useState(getInitSelectedProductsData());
    const [sizeSelectErrors, setSizeSelectErrors] = useState(new Array(productsCount).fill(false));
    const onProductChange = (index, data) => {
        const newSelectedProductsData = [...selectedProductsData];
        newSelectedProductsData[index] = data;
        setSelectedProductsData(newSelectedProductsData);
    };
    const handleAddToBasket = () => {
        var _a;
        const sizeErrors = selectedProductsData.map(item => (item.isSelected ? !item.sku : false));
        if (sizeErrors.some(error => error)) {
            setSizeSelectErrors(sizeErrors);
            const firstErrorIndex = sizeErrors.findIndex(item => item);
            if (isPDP && !isMobile && tilesWrapperRef.current) {
                const element = tilesWrapperRef.current.querySelectorAll('.as-m-product-tile')[firstErrorIndex];
                const scrollingOffset = ScrollUtil.getScrollingOffset(isDesktop, isTablet, show_sticky_bar_pdp);
                ScrollUtil.scrollToPosition(element.offsetTop - scrollingOffset);
            }
            else {
                const elementInPopover = (_a = multiContentRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.as-m-product-tile')[firstErrorIndex];
                ScrollUtil.scrollToElement(elementInPopover, true);
            }
        }
        else {
            onAddToBasket(selectedProductsData.filter(item => item.isSelected));
            onClose();
            setSelectedProductsData(getInitSelectedProductsData());
        }
    };
    const handleSetError = (error, index) => {
        setSizeSelectErrors(items => {
            const sizeErrors = [...items];
            sizeErrors[index] = error;
            return sizeErrors;
        });
    };
    const isModalContext = !isPDP || isMobile;
    useLockBodyScroll(isOpen && isModalContext);
    useEffect(() => {
        if (!isOpen) {
            setSizeSelectErrors(new Array(productsCount).fill(false));
        }
    }, [isOpen]);
    useEffect(() => {
        fetchDeliveryPromise(selectedProductsData.filter(product => product.isSelected && product.colorId && product.sku));
        fetchTotalPrice(selectedProductsData.filter(product => product.isSelected && product.colorId && product.sku));
    }, [selectedProductsData]);
    const numberProducts = selectedProductsData.filter(item => item.isSelected).length;
    const getTitleTranslation = () => {
        if (!isPDP) {
            return t('product.look.title');
        }
        return mainWebShop === MainWebShop.JUTTU ? t('product.look.pdp.title.specific') : t('product.look.pdp.title');
    };
    const multiTitle = popoverTitle || getTitleTranslation();
    const multiFooterContent = (_jsx(MiniPDPMultiFooter, { numberProducts: numberProducts, totalPrice: numberProducts ? totalPrice : undefined, currency: currency, handleAddToBasket: handleAddToBasket }));
    const multiProductTileListContent = (_jsx(Scrollable, { horizontal: false, scrollbarHidden: true, shadow: true, children: _jsxs(Box, { children: [_jsx(MiniPDPMultiProductTileList, { tileIdSuffix: "-popover", products: products, selectedProductsData: selectedProductsData, wishlistProducts: wishlistProducts, sizeSelectErrors: sizeSelectErrors, isShopLanding: isShopLanding, isPDP: isPDP, handleSetError: handleSetError, onWishListToggle: onWishListToggle, onProductChange: onProductChange }), !!deliveryPromise && !!numberProducts && (_jsx(Box, { paddingInline: { base: '4', md: '8' }, paddingBlockStart: "4", paddingBlockEnd: "10", children: _jsx(MiniPDPDeliveryPromise, { deliveryPromise: deliveryPromise, boxProps: { paddingBlockEnd: { base: '1', md: '2' } } }) }))] }) }));
    const multiTitleContent = (_jsx(Box, { paddingBlockEnd: "6", children: _jsx(TextAlpha, { as: "h2", variant: "headingS", children: multiTitle }) }));
    const popoverMiniPDPMulti = isMobile ? (_jsx(BottomSheet, { ariaLabel: multiTitle, footer: multiFooterContent, fullHeightOnScroll: true, hasCloseBtn: true, isOpen: isOpen, onClose: onClose, padding: "none", testId: generateTestId({ testId: TEST_ID, suffix: 'bottom-sheet' }), zIndex: "drawer", children: _jsxs("div", { ref: multiContentRef, children: [multiTitleContent, multiProductTileListContent] }) })) : (_jsx(Sidebar, { ariaLabel: multiTitle, customWidth: "480px", footer: { content: multiFooterContent, position: 'absolute' }, hasBackdrop: true, hasCloseBtn: true, hasCloseBtnSpacing: false, isOpen: isOpen, onClose: onClose, position: "fixed", side: "right", testId: generateTestId({ testId: TEST_ID, suffix: 'sidebar' }), zIndex: "drawer", children: _jsxs("div", { ref: multiContentRef, children: [multiTitleContent, multiProductTileListContent] }) }));
    const renderPopoverMiniPDPMulti = () => {
        if (!isClient)
            return null;
        return createPortal(popoverMiniPDPMulti, document.body);
    };
    if (isPDP) {
        return (_jsxs(_Fragment, { children: [isMobile && renderPopoverMiniPDPMulti(), _jsx(Breakpoint, { from: "md", children: _jsxs("div", { ref: tilesWrapperRef, children: [_jsx(Box, { paddingBlockEnd: { md: '4', lg: '6' }, paddingBlockStart: { md: '10' }, children: _jsx(TextAlpha, { as: "h2", variant: "subheadingXL", children: title || getTitleTranslation() }) }), _jsx(MiniPDPMultiProductTileList, { products: products, selectedProductsData: selectedProductsData, wishlistProducts: wishlistProducts, sizeSelectErrors: sizeSelectErrors, isShopLanding: isShopLanding, isPDP: isPDP, handleSetError: handleSetError, onWishListToggle: onWishListToggle, onProductChange: onProductChange }), !!numberProducts && totalPrice && (_jsx(Box, { paddingBlockStart: { md: '4' }, paddingBlockEnd: { md: '2' }, children: _jsxs(Stack, { align: "center", gap: "2", children: [_jsxs(TextAlpha, { as: "span", variant: "bodyS", children: [t('checkout:checkout.basket.total'), ":"] }), _jsx(Price, { size: "lg", currency: currency, priceActual: {
                                                formattedPrice: AsNumeral.formatAsCurrency(totalPrice.totalSellPrice),
                                            }, pricePrevious: totalPrice.totalSellPrice !== totalPrice.totalStandardPrice
                                                ? {
                                                    formattedPrice: AsNumeral.formatAsCurrency(totalPrice.totalStandardPrice),
                                                }
                                                : undefined })] }) })), !!deliveryPromise && !!numberProducts && (_jsx(MiniPDPDeliveryPromise, { deliveryPromise: deliveryPromise, boxProps: { paddingBlockEnd: { base: '1', md: '2' } } })), _jsx(Box, { paddingBlockStart: { md: '4' }, children: _jsx(MiniPDPMultiCTA, { amount: numberProducts, handleAddToBasket: handleAddToBasket }) })] }) })] }));
    }
    return createPortal(popoverMiniPDPMulti, document.body);
};
